/**
 * @file Database status button with sync status and collection management
 * @version 1.0.0
 * @since 2025-10-04
 */

import DataPortabilityPanel from '../data-portability/components/DataPortabilityPanel.js';
import { useDb } from '../core/db.js';
import { useNetworkStatus } from '../core/networkStatus.js';

const { ref, computed, onMounted, onUnmounted } = Vue;

export default {
  name: 'DatabaseStatusButton',

  inheritAttrs: false,

  components: {
    DataPortabilityPanel,
  },

  template: `
    <div class="dropdown">
      <!-- Trigger Button -->
      <button
        type="button"
        class="btn position-relative"
        :class="statusButton"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        v-bind="$attrs"
      >
        <i :class="statusIcon"></i>

        <!-- Error badge -->
        <span
          v-if="showErrorBadge"
          class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
          style="font-size: 0.6rem;"
        >
          !
        </span>

        <!-- Received badge -->
        <span
          v-if="showReceivedBadge"
          class="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-primary"
          style="font-size: 0.6rem;"
        >
          <i class="bi bi-arrow-down"></i> {{ receivedCount }}
        </span>

        <!-- Sent badge -->
        <span
          v-if="showSentBadge"
          class="position-absolute top-100 start-0 translate-middle badge rounded-pill bg-success"
          style="font-size: 0.6rem;"
        >
          <i class="bi bi-arrow-up"></i> {{ sentCount }}
        </span>
      </button>

      <!-- Dropdown Menu -->
      <ul class="dropdown-menu dropdown-menu-end p-0" style="min-width: 400px;">
        <!-- Offline message -->
        <li v-if="!networkStatus.isOnline" class="p-3 bg-warning bg-opacity-10">
          <div class="d-flex align-items-center">
            <i class="bi bi-wifi-off me-2"></i>
            <small>You're offline. Changes will sync when you reconnect.</small>
          </div>
        </li>

        <!-- Collections list -->
        <li class="p-3">
          <h6 class="mb-3">Collections</h6>
          <div v-for="col, name of collections" :key="col.schema.primaryKey" class="mb-2">
            <div class="d-flex justify-content-between align-items-center">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2">
                  <strong>{{ name }}</strong>
                  <span class="badge bg-secondary">{{ col.items?.length || 0 }}</span>
                  <span v-if="col.syncErrors.length" class="badge bg-danger">
                    {{ col.syncErrors.length }} error{{ col.syncErrors.length > 1 ? 's' : ''}}
                  </span>
                </div>
                <small class="text-info" v-if="col.isMigrating">
                  <i class="bi bi-arrow-repeat spin"></i> Migrating data<span v-if="col.migrationProgress?.total"> ({{ col.migrationProgress.handled }}/{{ col.migrationProgress.total }})</span>
                </small>
                <small class="text-muted" v-else-if="col.isInSync"><i class="bi bi-check-circle text-success"></i> Synchronized {{ formatDate(col.lastSuccessfulSync) }}</small>
                <small class="text-muted" v-else-if="col.isLocalOnly"><i class="bi bi-circle text-muted"></i> Local-only data</small>
                <small class="text-muted" v-else-if="!col.lastSuccessfulSync"><i class="bi bi-hourglass-split"></i> Waiting for initial sync</small>
                <small class="text-muted" v-else><i class="bi bi-x-circle text-warning"></i> Out of sync, last sync {{ formatDate(col.lastSuccessfulSync) }}</small>
                <small class="text-danger ms-1" v-if="col.lastSyncErrorMessage && !col.isMigrating && (col.syncErrors.length || (!col.isLocalOnly && !col.isInSync && col.lastSuccessfulSync))">{{ col.lastSyncErrorMessage }}</small>
              </div>
              <button
                v-if="!col.isLocalOnly"
                class="btn btn-sm btn-outline-secondary"
                @click="col.resync()"
                :disabled="!col.getReplicationState || col.isMigrating"
              >
                <i class="bi bi-arrow-repeat"></i>
              </button>
            </div>
          </div>
        </li>

        <li><hr class="dropdown-divider"></li>

        <!-- Action buttons -->
        <li class="px-3 py-2 d-flex justify-content-end align-items-center gap-2">
          <button
            class="btn btn-outline-secondary btn-sm"
            @click="db.resyncAll"
          >
            <i class="bi bi-arrow-repeat me-1"></i>
            Sync All
          </button>
          <button
            v-if="showHardResetButton"
            class="btn btn-outline-danger btn-sm"
            @click="db.hardReset"
          >
            <i class="bi bi-trash me-1"></i>
            Hard Reset
          </button>
          <button
            v-if="showDataButton"
            class="btn btn-outline-secondary btn-sm"
            data-bs-toggle="modal"
            data-bs-target="#dataPortabilityModal"
          >
            <i class="bi bi-database-fill-gear me-1"></i>
            Data
          </button>
        </li>
      </ul>

      <!-- Data Portability Modal -->
      <div class="modal fade" id="dataPortabilityModal" tabindex="-1" aria-labelledby="dataPortabilityModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-xl">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="dataPortabilityModalLabel">
                <i class="bi-database-fill-gear me-2"></i>
                Data Portability
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <DataPortabilityPanel />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,

  props: {
    syncedStatusDuration: {
      type: Number,
      default: 3000,
    },
    showDataButton: {
      type: Boolean,
      default: true,
    },
    showHardResetButton: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    formatDate(date) {
      if (window.VueUse !== undefined) {
        return VueUse.useTimeAgo(new Date(date));
      }
      return date.slice(0, 16).replace('T', ' ');
    },
  },

  setup(props) {
    const db = useDb();
    const networkStatus = useNetworkStatus();

    const collections = computed(() =>
      Object.fromEntries(db.collections.map(col => [col.name, col]))
    );

    // Batching state for received/sent badges
    const receivedCount = ref(0);
    const sentCount = ref(0);
    const lastReceivedTime = ref(null);
    const lastSentTime = ref(null);
    const showReceivedBadge = ref(false);
    const showSentBadge = ref(false);

    // Track if we recently synced for "synced" status
    const recentlySynced = ref(false);
    let syncedTimeout = null;

    // Force reactivity update for time display every 10 seconds
    const now = ref(Date.now());
    let updateInterval = null;

    // Has any collection ever completed a sync? Used to distinguish a real
    // out-of-sync regression from the initial-load window.
    const hasAnyPriorSync = computed(() =>
      db.collections.some(col => col.lastSuccessfulSync)
    );

    // Error badge: only show real sync failures, never during migration or
    // the initial load window before any collection has synced.
    const showErrorBadge = computed(() => {
      if (!networkStatus.isOnline) return false;
      if (db.isMigrating) return false;
      if (!hasAnyPriorSync.value) return false;
      return !db.isInSync;
    });

    // Status icon computation
    const statusIcon = computed(() => {
      if (!networkStatus.isOnline) {
        return 'bi bi-wifi-off';
      }
      if (db.isMigrating) {
        return 'bi bi-arrow-repeat spin';
      }
      if (db.isSyncing) {
        return 'bi bi-arrow-repeat spin';
      }
      if (recentlySynced.value && db.isInSync) {
        return 'bi bi-check-circle';
      }
      if (recentlySynced.value && !db.isInSync) {
        return 'bi bi-x-circle';
      }
      return 'bi bi-database';
    });

    // Button class
    const statusButton = computed(() => {
      if (!networkStatus.isOnline) {
        return 'btn-warning';
      }
      if (db.isMigrating) {
        return 'btn-info';
      }
      if (db.isSyncing) {
        return 'btn-info';
      }
      if (recentlySynced.value && db.isInSync) {
        return 'btn-info';
      }
      if (recentlySynced.value && !db.isInSync) {
        return 'btn-warning';
      }
      return 'btn-secondary';
    });

    // Event handlers for batching
    const handleDocumentReceived = event => {
      const now = event.detail.timestamp;

      if (
        lastReceivedTime.value &&
        now - lastReceivedTime.value < props.syncedStatusDuration
      ) {
        receivedCount.value++;
      } else {
        receivedCount.value = 1;
      }

      lastReceivedTime.value = now;
      showReceivedBadge.value = true;

      setTimeout(() => {
        if (Date.now() - lastReceivedTime.value >= props.syncedStatusDuration) {
          showReceivedBadge.value = false;
        }
      }, props.syncedStatusDuration);
    };

    const handleDocumentSent = event => {
      const now = event.detail.timestamp;

      if (
        lastSentTime.value &&
        now - lastSentTime.value < props.syncedStatusDuration
      ) {
        sentCount.value++;
      } else {
        sentCount.value = 1;
      }

      lastSentTime.value = now;
      showSentBadge.value = true;

      setTimeout(() => {
        if (Date.now() - lastSentTime.value >= props.syncedStatusDuration) {
          showSentBadge.value = false;
        }
      }, props.syncedStatusDuration);
    };

    const triggerSyncedStatus = () => {
      if (syncedTimeout) {
        clearTimeout(syncedTimeout);
      }
      recentlySynced.value = true;
      syncedTimeout = setTimeout(() => {
        recentlySynced.value = false;
      }, props.syncedStatusDuration);
    };

    const formatTimeSince = ms => {
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      return `${hours}h ago`;
    };

    Vue.watch(
      () => db.isSyncing,
      () => {
        if (!db.isSyncing) {
          triggerSyncedStatus();
        }
      }
    );

    // Set up event listeners
    onMounted(() => {
      db.events.addEventListener('document-received', handleDocumentReceived);
      db.events.addEventListener('document-sent', handleDocumentSent);

      // Update time every 10 seconds to refresh time display
      updateInterval = setInterval(() => {
        now.value = Date.now();
      }, 10000);
    });

    onUnmounted(() => {
      db.events.removeEventListener(
        'document-received',
        handleDocumentReceived
      );
      db.events.removeEventListener('document-sent', handleDocumentSent);
      if (syncedTimeout) {
        clearTimeout(syncedTimeout);
      }
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    });

    return {
      db,
      networkStatus,
      collections,
      statusIcon,
      statusButton,
      showErrorBadge,
      receivedCount,
      sentCount,
      showReceivedBadge,
      showSentBadge,
      showDataButton: computed(() => props.showDataButton),
      showHardResetButton: computed(() => props.showHardResetButton),
    };
  },
};
